import { lazy } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MyCentralHeader } from '../styles';
import { PrescriptionService } from '../../prescription/service';
import Avatar from '../../../components/UI-CHAKRA/Avatar';
import colorContrast from 'color-contrast';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Button } from '../../../components/UI-CHAKRA/Button';
import { ReactComponent as PDFIcon } from '../../../assets/images/icons/mevo/pdf-icon.svg';
import { FormControl } from '../../../components/UI-CHAKRA/FormControl';
import { PDF_LAYOUT_MODEL_TYPE, PDFModelLayout } from '../../../shared/prescriber';
import { get, isEmpty } from 'lodash';
import BaseDialog from '../../../components/Dialogs/BaseDialog';
import { StandaloneCTA } from './StandaloneCTA';
import { CustomMenuItem, CustomSelect } from '../../../components/UI-CHAKRA/Select/CustomSelect';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { FormLabel } from '../../../components/UI-CHAKRA/FormLabel';
import withSuspense from '../../../components/withLazyComponent';
import { DONT_SHOW_RQE_IN_PDF_KEY } from '../constants';

const PDFLayoutEditor = withSuspense(lazy(() => import('./PDFLayoutEditor')));
const PDFLayoutList = withSuspense(lazy(() => import('./PDFLayoutList')));

export enum LAYOUT_ACTION_TYPES {
  EDIT = 'edit',
  CREATE = 'create',
}

export interface CreateEditLayout {
  layout: PDFModelLayout | null;
  actionType: LAYOUT_ACTION_TYPES;
  layoutType: PDF_LAYOUT_MODEL_TYPE;
}

function PrescriberCentral() {
  const { selectedRqe, setSelectedRqe, rqes } = usePrescriptionStore();

  const primaryColor = usePrescriptionStore((store) => store.primaryColor);

  const prescriptionId = usePrescriptionStore((store) => store.prescription?.idPrescricao);
  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const prescriberId = usePrescriptionStore((store) => store.prescriber()?.idPrescritor || null);

  const { prescriberConfiguration } = usePrescriptionStore();

  const enableCustomPdfLayout = get(prescriberConfiguration, 'Features.enablePdfCustomLayout', false);

  const [isPdfListOpen, setIsPdfListOpen] = useState(false);
  const [createEditLayout, setCreateEditLayout] = useState<CreateEditLayout | null>(null);

  const getSimpleText = (rqeText: string): string => {
    if (!rqeText) return '';
    const [textToShow] = rqeText.split(' - RQE Nº:');
    return textToShow;
  };

  const prescriberNameFirstLetter = useMemo(() => {
    if (!prescriber) return;
    return prescriber.Nome.charAt(0) || '';
  }, [prescriber]);

  const PrescriberName = useMemo(() => {
    if (!prescriber) return;

    return (
      <Box padding={'0 20px'}>
        <Text
          color={'typography.darkest'}
          align="center"
          fontSize={'15px'}
          fontWeight={'700'}
          marginTop={'8px'}
          lineHeight={'22px'}
        >
          Dr(a) {prescriber.Nome}
        </Text>
      </Box>
    );
  }, [prescriber]);

  const handleChange = useCallback(
    async (value: number) => {
      if (!prescriptionId || !prescriberId) return;

      const isDeselect = value === DONT_SHOW_RQE_IN_PDF_KEY;
      const selectedRqe = rqes.find((rqe) => rqe.idMedicoRqe === value) ?? null;

      setSelectedRqe(isDeselect ? DONT_SHOW_RQE_IN_PDF_KEY : value);

      await PrescriptionService.changeSelectedRqe(prescriptionId, {
        idMedico: prescriberId,
        RQESelecionado: isDeselect ? null : selectedRqe,
      });
    },
    [prescriptionId, prescriberId, rqes, setSelectedRqe]
  );

  const MedicalRegister = useMemo(() => {
    if (!prescriber) return;
    return (
      <Box padding={'0 20px'} mt={'2px'}>
        <Text fontWeight={400} color={'typography.darkest'} fontSize={'14px'} align={'center'}>
          {prescriber.Conselho}: {prescriber.Numero} / {prescriber.UFConselho}
        </Text>
      </Box>
    );
  }, [prescriber]);

  const MenuRqes = useMemo(() => {
    if (!prescriptionId) return;

    return rqes.map((rqe) => {
      if (!rqe.ativo) return null;

      return (
        <CustomMenuItem
          key={rqe.idMedicoRqe}
          value={rqe.idMedicoRqe}
          label={getSimpleText(rqe.textoSimples)}
          title={rqe.textoSimples}
          onClick={() => handleChange(rqe.idMedicoRqe)}
        />
      );
    });
  }, [handleChange, prescriptionId, rqes]);

  const RQERegister = useMemo(() => {
    const disableRqe = get(prescriberConfiguration, 'Features.disableRqe', false);

    if (!prescriptionId || disableRqe) return;

    const rqesMap = (rqes || []).reduce(
      (acc, rqe) => {
        return {
          ...acc,
          [rqe.idMedicoRqe]: rqe.textoSimples,
        };
      },
      {
        [DONT_SHOW_RQE_IN_PDF_KEY]: 'Sem Especialidade',
      }
    );

    const value = selectedRqe ? get(rqesMap, selectedRqe, '') : '';

    return (
      <Box marginTop={'20px'} width={'100%'} padding={'0 23px'}>
        <FormControl w={'100%'}>
          <FormLabel>RQE exibido na Prescrição</FormLabel>
          <CustomSelect value={value} placeholder={'RQE exibido na Prescrição'}>
            <>
              <CustomMenuItem
                key={DONT_SHOW_RQE_IN_PDF_KEY}
                value={DONT_SHOW_RQE_IN_PDF_KEY}
                label={'Sem Especialidade'}
                onClick={() => handleChange(DONT_SHOW_RQE_IN_PDF_KEY)}
              />
              {MenuRqes}
            </>
          </CustomSelect>
        </FormControl>
      </Box>
    );
  }, [prescriberConfiguration, prescriptionId, rqes, selectedRqe, MenuRqes, handleChange]);

  const StandaloneMarketing = useMemo(() => {
    const disableStandaloneMarketing = get(prescriberConfiguration, 'Features.disableStandaloneMarketing', false);

    if (disableStandaloneMarketing) return;

    return <StandaloneCTA />;
  }, [prescriberConfiguration]);

  useEffect(() => {
    if (!selectedRqe) {
      handleChange(DONT_SHOW_RQE_IN_PDF_KEY);
    }
  }, [handleChange, selectedRqe]);

  useEffect(() => {
    if (selectedRqe) return;

    if (rqes?.length === 1) {
      const unicoRqe = rqes[0];
      handleChange(unicoRqe.idMedicoRqe);
      return;
    }

    if (rqes?.length > 1) {
      const maiorRqe = rqes.reduce((a, b) => (a.idMedicoRqe > b.idMedicoRqe ? a : b));
      handleChange(maiorRqe.idMedicoRqe);
      return;
    }

    handleChange(DONT_SHOW_RQE_IN_PDF_KEY)
  }, [rqes, selectedRqe, handleChange])


  return (
    <div id="PrescriberCentral">
      <MyCentralHeader title={'Meus dados'} />
      <Box marginTop={'20px'} display={'flex'} alignItems={'center'} flexDirection={'column'}>
        <Avatar
          bgColor={'primary.main'}
          color={colorContrast('#fff', primaryColor()) > 3 ? 'white' : '#1E252B'}
          fontWeight={'700'}
          fontSize={'32px'}
          w={'55px'}
          h={'55px'}
          name={prescriberNameFirstLetter}
        ></Avatar>
        {PrescriberName}
        {MedicalRegister}
        {RQERegister}

        {StandaloneMarketing}

        {enableCustomPdfLayout && (
          <Box w={'100%'} padding={'0 20px'} mb={'20px'} mt={'20px'}>
            <Button
              onClick={() => setIsPdfListOpen(true)}
              variant={'outline'}
              w={'100%'}
              borderRadius={'40px'}
              borderWidth={'2px'}
              leftIcon={<PDFIcon />}
            >
              Layout de PDF
            </Button>
          </Box>
        )}

        <BaseDialog
          isOpen={isPdfListOpen}
          persist={false}
          onClose={() => setIsPdfListOpen(false)}
          width={{ base: '95%', md: '650px' }}
          stylesCustom={{
            minHeight: '65vh',
          }}
        >
          <PDFLayoutList
            onClose={() => setIsPdfListOpen(false)}
            onEdit={(layout: PDFModelLayout | null, layoutType: 'SIMPLES' | 'CONTROLADO') => {
              setIsPdfListOpen(false);
              setCreateEditLayout({ actionType: LAYOUT_ACTION_TYPES.EDIT, layout, layoutType });
            }}
            onCreate={(layoutType: 'SIMPLES' | 'CONTROLADO') => {
              setIsPdfListOpen(false);
              setCreateEditLayout({ actionType: LAYOUT_ACTION_TYPES.CREATE, layout: null, layoutType });
            }}
            isWidget={false}
          />
        </BaseDialog>

        <BaseDialog
          isOpen={!isEmpty(createEditLayout)}
          persist={false}
          onClose={() => setCreateEditLayout(null)}
          width={'95vw'}
          stylesCustom={{
            backgroundColor: 'transparent',
            height: '95vh',
            boxShadow: 'none',
          }}
        >
          <PDFLayoutEditor
            goBack={() => {
              setCreateEditLayout(null);
              setIsPdfListOpen(true);
            }}
            layout={get(createEditLayout, 'layout', null)}
            actionType={get(createEditLayout, 'actionType', LAYOUT_ACTION_TYPES.EDIT)}
            layoutType={get(createEditLayout, 'layoutType', 'SIMPLES')}
          />
        </BaseDialog>
      </Box>
    </div>
  );
}

export default PrescriberCentral;
